import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import React, { useMemo, useState } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { MEDIA_QUERIES, getResponsiveValue } from "../responsive/mediaQueries";
import { getContentStyle } from "./getContentStyle";

// Function to generate deterministic colors based on speaker name
// This ensures the same speaker always gets the same color
const getColorForSpeaker = (speaker) => {
    // Simple hash function to convert speaker name to a number
    let hash = 0;
    for (let i = 0; i < speaker.length; i++) {
        hash = ((hash << 5) - hash) + speaker.charCodeAt(i);
        hash = hash & hash; // Convert to 32bit integer
    }

    // Use the hash to generate a color in the range 6789ABCDEF (brighter colors)
    const letters = '6789ABCDEF';
    let color = '#';
    // Use the absolute value of hash to ensure positive numbers
    const absHash = Math.abs(hash);
    for (let i = 0; i < 6; i++) {
        // Use different bits of the hash for each position in the color
        const index = (absHash >> (i * 4)) & 0xF;
        color += letters[index % letters.length];
    }
    return color;
};

const ChartBox = ({ title, description, width, height, mb, mt, children }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const popoverTimeoutRef = React.useRef(); // Ref to store timeout ID

    const handlePopoverOpen = (event) => {
        clearTimeout(popoverTimeoutRef.current); // Clear any existing timeout
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        // Set a timeout to close the popover. This allows the user to move the mouse
        // from the icon to the popover without it closing immediately.
        popoverTimeoutRef.current = setTimeout(() => {
            setAnchorEl(null);
        }, 100); // Adjust delay (in ms) as needed
    };

    const handlePopoverMouseEnter = () => {
        clearTimeout(popoverTimeoutRef.current); // Cancel closing if mouse enters popover
    };

    const handlePopoverMouseLeave = () => {
        handlePopoverClose(); // Close if mouse leaves popover
    };

    const open = Boolean(anchorEl);

    return (
        <Box sx={{ width, height, display: 'flex', flexDirection: 'column', alignItems: 'center', mb, mt, position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative', mb: 1 }}>
                <IconButton
                    onMouseEnter={handlePopoverOpen}
                    onMouseLeave={handlePopoverClose}
                    size="small"
                    sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', padding: '2px' }}
                >
                    <InfoOutlinedIcon fontSize="inherit" sx={{ fontSize: '1rem' }} />
                </IconButton>
                <Typography variant="h6" sx={{ textAlign: 'center', fontSize: '0.8rem', ml: 3 }}>
                    {title}
                </Typography>
            </Box>
            {children}
            <Popover
                sx={{ pointerEvents: 'none' }}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    onMouseEnter: handlePopoverMouseEnter,
                    onMouseLeave: handlePopoverMouseLeave,
                    sx: { p: 1.5, maxWidth: '250px', backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', pointerEvents: 'auto' }
                }}
                disableRestoreFocus
            >
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{description}</Typography>
            </Popover>
        </Box>
    );
};

const ChartsGrid = ({ toolbarPosition, data }) => {
    const theme = useTheme();

    // Use media query hooks to detect screen size using the existing responsive system
    const isXSmallScreen = useMediaQuery(MEDIA_QUERIES.xs);
    const isSmallScreen = useMediaQuery(MEDIA_QUERIES.sm);
    const isMediumScreen = useMediaQuery(MEDIA_QUERIES.md);
    const isLargeScreen = useMediaQuery(MEDIA_QUERIES.lg);

    const SENTIMENT_COLORS = {
        positive: theme.palette.success.main,
        negative: theme.palette.error.main,
        neutral: theme.palette.grey[500]
    };

    const LENGTH_BIN_COLOR = theme.palette.info.main;
    const SECONDS_BIN_COLOR = theme.palette.warning.main;
    const SENTENCE_SPACE_COLORS = {
        sentences: theme.palette.primary.main,
        spaces: theme.palette.grey[400]
    };

    // Process data for charts
    const {
        sentimentCounts,
        speakerCounts,
        lengthCounts,
        secondsCounts,
        sentenceSpaceCounts,
        sentenceSpaceSecondsCounts,
        speakerSentimentCounts,
        speakerStats,
        sentimentStats
    } = useMemo(() => {
        return data.reduce((acc, row) => {
            const secondsInt = parseInt(row.sec_int) || 0; // Get the integer seconds

            if (!row.space) { // Exclude space rows FOR MOST COUNTS
                const sentiment = row.sentiment;
                const speaker = row.speaker;
                const length = row.length;
                const seconds = parseFloat(row.sec) || 0; // Convert to number and default to 0 if NaN

                // Overall counts
                acc.sentimentCounts[sentiment] = (acc.sentimentCounts[sentiment] || 0) + 1;
                acc.speakerCounts[speaker] = (acc.speakerCounts[speaker] || 0) + 1;
                acc.lengthCounts[length] = (acc.lengthCounts[length] || 0) + 1;
                acc.secondsCounts[seconds] = (acc.secondsCounts[seconds] || 0) + 1;
                acc.sentenceSpaceCounts.sentences++; // Count non-space row
                acc.sentenceSpaceSecondsCounts.sentencesSeconds += secondsInt; // Sum seconds for sentences

                // Sentiment per speaker counts
                if (!acc.speakerSentimentCounts[speaker]) {
                    acc.speakerSentimentCounts[speaker] = { positive: 0, negative: 0, neutral: 0 };
                }
                acc.speakerSentimentCounts[speaker][sentiment]++;

                // Stats per speaker (for averages)
                if (!acc.speakerStats[speaker]) {
                    acc.speakerStats[speaker] = { totalLength: 0, totalSeconds: 0, sentenceCount: 0 };
                }
                acc.speakerStats[speaker].totalLength += length;
                acc.speakerStats[speaker].totalSeconds += seconds;
                acc.speakerStats[speaker].sentenceCount++;

                // Stats per sentiment (for averages)
                if (!acc.sentimentStats[sentiment]) {
                    acc.sentimentStats[sentiment] = { totalLength: 0, totalSeconds: 0, sentenceCount: 0 };
                }
                acc.sentimentStats[sentiment].totalLength += length;
                acc.sentimentStats[sentiment].totalSeconds += seconds;
                acc.sentimentStats[sentiment].sentenceCount++;

            } else {
                acc.sentenceSpaceCounts.spaces++; // Count space row
                acc.sentenceSpaceSecondsCounts.spacesSeconds += secondsInt; // Sum seconds for spaces
            }
            return acc;
        }, {
            sentimentCounts: {},
            speakerCounts: {},
            lengthCounts: {},
            secondsCounts: {},
            sentenceSpaceCounts: { sentences: 0, spaces: 0 },
            sentenceSpaceSecondsCounts: { sentencesSeconds: 0, spacesSeconds: 0 },
            speakerSentimentCounts: {},
            speakerStats: {}, // Initialize new accumulator
            sentimentStats: {} // Initialize new accumulator
        });
    }, [data]);

    // Use useMemo to create speaker colors that won't change on re-renders
    const speakerColors = useMemo(() => {
        const speakers = Object.keys(speakerCounts);
        return speakers.reduce((acc, speaker) => {
            acc[speaker] = getColorForSpeaker(speaker);
            return acc;
        }, {});
    }, [speakerCounts]);

    // Use useMemo for all the chart data preparation
    const sentimentChartData = useMemo(() => {
        return Object.keys(sentimentCounts).map(key => ({
            name: key.charAt(0).toUpperCase() + key.slice(1),
            value: sentimentCounts[key],
            color: SENTIMENT_COLORS[key]
        }));
    }, [sentimentCounts, SENTIMENT_COLORS]);

    const speakerChartData = useMemo(() => {
        const speakers = Object.keys(speakerCounts);
        return speakers.map(key => ({
            name: key,
            value: speakerCounts[key],
            color: speakerColors[key]
        })).sort((a, b) => a.name.localeCompare(b.name));
    }, [speakerCounts, speakerColors]);

    const lengthChartData = useMemo(() => {
        return Object.keys(lengthCounts).map(length => ({
            name: String(length), // X-axis label is the exact length
            count: lengthCounts[length],
        })).sort((a, b) => parseInt(a.name) - parseInt(b.name));
    }, [lengthCounts]);

    // Generate seconds chart data based on exact counts
    const secondsChartData = useMemo(() => {
        return Object.keys(secondsCounts).map(second => ({
            name: String(second),
            count: secondsCounts[second],
        })).sort((a, b) => parseInt(a.name) - parseInt(b.name));
    }, [secondsCounts]);

    const sentenceSpaceChartData = useMemo(() => {
        return [
            { name: 'Sentences', value: sentenceSpaceCounts.sentences, color: SENTENCE_SPACE_COLORS.sentences },
            { name: 'Spaces', value: sentenceSpaceCounts.spaces, color: SENTENCE_SPACE_COLORS.spaces },
        ];
    }, [sentenceSpaceCounts, SENTENCE_SPACE_COLORS]);

    const sentenceSpaceSecondsChartData = useMemo(() => {
        return [
            { name: 'Speaking', value: sentenceSpaceSecondsCounts.sentencesSeconds, color: SENTENCE_SPACE_COLORS.sentences },
            { name: 'Spaces', value: sentenceSpaceSecondsCounts.spacesSeconds, color: SENTENCE_SPACE_COLORS.spaces },
        ];
    }, [sentenceSpaceSecondsCounts, SENTENCE_SPACE_COLORS]);

    // Prepare data for Speaker Sentiment Chart
    const speakerSentimentChartData = useMemo(() => {
        return Object.keys(speakerSentimentCounts).map(speaker => ({
            speaker: speaker,
            ...speakerSentimentCounts[speaker]
        })).sort((a, b) => a.speaker.localeCompare(b.speaker)); // Sort alphabetically
    }, [speakerSentimentCounts]);

    // Define sentiment order once
    const sentimentOrder = ['Positive', 'Neutral', 'Negative'];

    // Calculate and prepare data for Average Length/Seconds per Sentiment
    const { avgLengthPerSentimentData, avgSecondsPerSentimentData } = useMemo(() => {
        const lengthData = [];
        const secondsData = [];

        Object.keys(sentimentStats).forEach(sentiment => {
            const stats = sentimentStats[sentiment];
            const sentimentName = sentiment.charAt(0).toUpperCase() + sentiment.slice(1); // Capitalize

            if (stats.sentenceCount > 0) {
                lengthData.push({
                    sentiment: sentimentName,
                    avgLength: parseFloat((stats.totalLength / stats.sentenceCount).toFixed(1)), // Calculate avg, format to 1 decimal
                    color: SENTIMENT_COLORS[sentiment]
                });
                secondsData.push({
                    sentiment: sentimentName,
                    avgSeconds: parseFloat((stats.totalSeconds / stats.sentenceCount).toFixed(1)), // Calculate avg, format to 1 decimal
                    color: SENTIMENT_COLORS[sentiment]
                });
            }
        });

        // Sort by sentiment name for consistent order
        lengthData.sort((a, b) => sentimentOrder.indexOf(a.sentiment) - sentimentOrder.indexOf(b.sentiment));
        secondsData.sort((a, b) => sentimentOrder.indexOf(a.sentiment) - sentimentOrder.indexOf(b.sentiment));

        return {
            avgLengthPerSentimentData: lengthData,
            avgSecondsPerSentimentData: secondsData
        };
    }, [sentimentStats, SENTIMENT_COLORS, sentimentOrder]);

    // Calculate and prepare data for Average Length/Seconds per Speaker and Efficiency
    const { avgLengthPerSpeakerData, avgSecondsPerSpeakerData, speakingEfficiencyData } = useMemo(() => {
        const lengthData = [];
        const secondsData = [];
        const efficiencyData = [];

        Object.keys(speakerStats).forEach(speaker => {
            const stats = speakerStats[speaker];

            if (stats.sentenceCount > 0) {
                lengthData.push({
                    speaker: speaker,
                    avgLength: parseFloat((stats.totalLength / stats.sentenceCount).toFixed(1)),
                    color: speakerColors[speaker]
                });
                secondsData.push({
                    speaker: speaker,
                    avgSeconds: parseFloat((stats.totalSeconds / stats.sentenceCount).toFixed(1)),
                    color: speakerColors[speaker]
                });

                // Calculate speaking efficiency (characters per second)
                const efficiency = stats.totalSeconds > 0 ? (stats.totalLength / stats.totalSeconds) : 0;
                efficiencyData.push({
                    speaker: speaker,
                    efficiency: parseFloat(efficiency.toFixed(1)),
                    color: speakerColors[speaker]
                });
            }
        });

        return {
            avgLengthPerSpeakerData: lengthData.sort((a, b) => a.speaker.localeCompare(b.speaker)),
            avgSecondsPerSpeakerData: secondsData.sort((a, b) => a.speaker.localeCompare(b.speaker)),
            speakingEfficiencyData: efficiencyData.sort((a, b) => a.speaker.localeCompare(b.speaker))
        };
    }, [speakerStats, speakerColors]);

    // Responsive chart layout using the existing responsive system
    const chartWidth = getResponsiveValue(
        isXSmallScreen,
        isSmallScreen,
        isMediumScreen,
        isLargeScreen,
        {
            xs: '100%',     // 1 chart per row on mobile
            sm: '48%',      // 2 charts per row on small screens
            md: '32%',      // 3 charts per row on medium screens
            default: '24%'  // 4 charts per row on large screens
        },
        '24%'
    );

    const chartHeight = getResponsiveValue(
        isXSmallScreen,
        isSmallScreen,
        isMediumScreen,
        isLargeScreen,
        {
            xs: '300px',    // Fixed height on mobile for better visibility
            sm: '320px',    // Slightly larger on small screens
            md: '28%',      // Percentage-based on medium screens
            default: '30%'  // 3 rows on large screens
        },
        '30%'
    );

    const chartMarginBottom = getResponsiveValue(
        isXSmallScreen,
        isSmallScreen,
        isMediumScreen,
        isLargeScreen,
        {
            xs: 2,          // More space between charts on mobile
            sm: 1.5,
            md: 1,
            default: 1
        },
        1
    );

    const chartMarginTop = getResponsiveValue(
        isXSmallScreen,
        isSmallScreen,
        isMediumScreen,
        isLargeScreen,
        {
            xs: 1,
            sm: 1,
            md: 1,
            default: 1
        },
        1
    );

    // Responsive chart margins and settings
    const chartMargins = {
        pie: getResponsiveValue(
            isXSmallScreen,
            isSmallScreen,
            isMediumScreen,
            isLargeScreen,
            {
                xs: { top: 10, right: 5, bottom: 15, left: 5 },
                sm: { top: 15, right: 8, bottom: 20, left: 8 },
                md: { top: 18, right: 10, bottom: 25, left: 10 },
                default: { top: 20, right: 10, bottom: 30, left: 10 }
            },
            { top: 20, right: 10, bottom: 30, left: 10 }
        ),
        bar: getResponsiveValue(
            isXSmallScreen,
            isSmallScreen,
            isMediumScreen,
            isLargeScreen,
            {
                xs: { top: 5, right: 5, left: -15, bottom: 5 },
                sm: { top: 5, right: 5, left: -20, bottom: 5 },
                md: { top: 5, right: 5, left: -25, bottom: 5 },
                default: { top: 5, right: 5, left: -25, bottom: 5 }
            },
            { top: 5, right: 5, left: -25, bottom: 5 }
        )
    };

    const responsiveFontSizes = {
        legend: getResponsiveValue(
            isXSmallScreen,
            isSmallScreen,
            isMediumScreen,
            isLargeScreen,
            {
                xs: '0.6rem',
                sm: '0.65rem',
                md: '0.7rem',
                default: '0.7rem'
            },
            '0.7rem'
        ),
        tick: getResponsiveValue(
            isXSmallScreen,
            isSmallScreen,
            isMediumScreen,
            isLargeScreen,
            {
                xs: 8,
                sm: 9,
                md: 10,
                default: 10
            },
            10
        ),
        tooltip: getResponsiveValue(
            isXSmallScreen,
            isSmallScreen,
            isMediumScreen,
            isLargeScreen,
            {
                xs: '0.7rem',
                sm: '0.75rem',
                md: '0.8rem',
                default: '0.8rem'
            },
            '0.8rem'
        )
    };

    const responsiveRadius = getResponsiveValue(
        isXSmallScreen,
        isSmallScreen,
        isMediumScreen,
        isLargeScreen,
        {
            xs: '70%',      // Larger radius on mobile for better visibility
            sm: '65%',
            md: '60%',
            default: '60%'
        },
        '60%'
    );

    // Responsive container justification
    const containerJustifyContent = getResponsiveValue(
        isXSmallScreen,
        isSmallScreen,
        isMediumScreen,
        isLargeScreen,
        {
            xs: 'center',       // Center single chart on mobile
            sm: 'space-around', // Space around 2 charts on small screens
            md: 'space-between', // Space between 3 charts on medium screens
            default: 'space-between' // Space between 4 charts on large screens
        },
        'space-between'
    );

    // --- Chart Descriptions ---

    const descriptions = {
        sentimentOverall: "Distribution of all sentences categorized by sentiment (Positive, Negative, Neutral).",
        speakersOverall: "Distribution of all sentences attributed to each speaker.",
        sentencesVsSpaces: "Ratio of lines containing text (Sentences) versus empty lines or explicit pauses (Spaces).",
        speakingVsSilence: "Total duration (in seconds) of speaking time versus silence/pause time.",
        sentimentBySpeaker: "Stacked bar chart showing the sentiment breakdown (Positive, Negative, Neutral) for each individual speaker's sentences.",
        avgLenBySentiment: "Average sentence length (in characters) for each sentiment category.",
        avgLenBySpeaker: "Average sentence length (in characters) for each speaker.",
        avgTimeBySentiment: "Average sentence duration (in seconds) for each sentiment category.",
        avgTimeBySpeaker: "Average sentence duration (in seconds) for each speaker.",
        speakingEfficiency: "Speaking efficiency (characters per second) for each speaker, showing who communicates most efficiently.",
        lengthDistr: "Distribution showing the count of sentences for each specific length (number of characters).",
        timeDistr: "Distribution showing the count of sentences for each specific duration (in seconds)."
    };

    return (
        <Box sx={{
            ...getContentStyle(toolbarPosition),
            width: '100%',
            height: 'calc(100vh - 64px)',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: containerJustifyContent,
            p: 1,
            pb: '60px',
            pr: '18px',
            boxSizing: 'border-box',
            overflow: 'auto'
        }}>

            {/* Sentiment Chart */}
            <ChartBox
                title="Sentiment (Overall)"
                description={descriptions.sentimentOverall}
                width={chartWidth}
                height={chartHeight}
                mb={chartMarginBottom}
            >
                {sentimentChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={chartMargins.pie}>
                            <Pie
                                data={sentimentChartData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius={responsiveRadius}
                                fill="#8884d8"
                                dataKey="value"
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                                {sentimentChartData.map((entry, index) => (
                                    <Cell key={`cell-sentiment-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: responsiveFontSizes.legend }} />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <Typography>No sentiment data available.</Typography>
                )}
            </ChartBox>

            {/* Speaker Chart */}
            <ChartBox
                title="Speakers (Overall)"
                description={descriptions.speakersOverall}
                width={chartWidth}
                height={chartHeight}
                mb={chartMarginBottom}
            >
                {speakerChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={chartMargins.pie}>
                            <Pie
                                data={speakerChartData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius={responsiveRadius}
                                fill="#82ca9d"
                                dataKey="value"
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                                {speakerChartData.map((entry, index) => (
                                    <Cell key={`cell-speaker-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: responsiveFontSizes.legend }} />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <Typography>No speaker data available.</Typography>
                )}
            </ChartBox>

            {/* Sentence vs Space Chart */}
            <ChartBox
                title="Sentences vs Spaces"
                description={descriptions.sentencesVsSpaces}
                width={chartWidth}
                height={chartHeight}
                mb={chartMarginBottom}
            >
                {sentenceSpaceChartData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={chartMargins.pie}>
                            <Pie
                                data={sentenceSpaceChartData}
                                cx="50%"
                                cy="50%"
                                innerRadius="30%" // Make it a donut chart
                                outerRadius={responsiveRadius}
                                fill="#8884d8"
                                dataKey="value"
                                labelLine={false}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                                {sentenceSpaceChartData.map((entry, index) => (
                                    <Cell key={`cell-sent-space-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <Typography>No data available.</Typography>
                )}
            </ChartBox>

            {/* Speaking Time vs Silence Time Chart */}
            <ChartBox
                title="Speaking vs Silence Time"
                description={descriptions.speakingVsSilence}
                width={chartWidth}
                height={chartHeight}
                mb={chartMarginBottom}
            >
                {sentenceSpaceSecondsChartData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={chartMargins.pie}>
                            <Pie
                                data={sentenceSpaceSecondsChartData}
                                cx="50%"
                                cy="50%"
                                innerRadius="30%" // Make it a donut chart
                                outerRadius={responsiveRadius}
                                fill="#8884d8"
                                dataKey="value"
                                labelLine={false}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                                {sentenceSpaceSecondsChartData.map((entry, index) => (
                                    <Cell key={`cell-speaking-silence-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip
                                contentStyle={{ backgroundColor: theme.palette.background.paper, fontSize: responsiveFontSizes.tooltip }}
                                itemStyle={{ color: theme.palette.text.primary }}
                                formatter={(value, name) => [`${value} seconds`, name]}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <Typography>No data available.</Typography>
                )}
            </ChartBox>

            {/* Sentiment per Speaker Chart */}
            <ChartBox
                title="Sentiment by Speaker"
                description={descriptions.sentimentBySpeaker}
                width={chartWidth}
                height={chartHeight}
                mb={chartMarginBottom}
            >
                {speakerSentimentChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={speakerSentimentChartData}
                            margin={chartMargins.bar}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="speaker" tick={{ fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} />
                            <YAxis tick={{ fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} />
                            <Tooltip
                                contentStyle={{ backgroundColor: theme.palette.background.paper, fontSize: responsiveFontSizes.tooltip }}
                                itemStyle={{ color: theme.palette.text.primary }}
                                formatter={(value, name) => [`${value} sentences`, name.charAt(0).toUpperCase() + name.slice(1)]}
                            />
                            <Legend wrapperStyle={{ fontSize: responsiveFontSizes.legend }} />
                            <Bar dataKey="positive" stackId="a" fill={SENTIMENT_COLORS.positive} name="Positive" />
                            <Bar dataKey="negative" stackId="a" fill={SENTIMENT_COLORS.negative} name="Negative" />
                            <Bar dataKey="neutral" stackId="a" fill={SENTIMENT_COLORS.neutral} name="Neutral" />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <Typography>No speaker sentiment data available.</Typography>
                )}
            </ChartBox>

            {/* Avg Length per Sentiment Chart */}
            <ChartBox
                title="Avg Len by Sentiment"
                description={descriptions.avgLenBySentiment}
                width={chartWidth}
                height={chartHeight}
                mb={chartMarginBottom}
            >
                {avgLengthPerSentimentData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={avgLengthPerSentimentData} layout="vertical" margin={{ top: 0, right: 15, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" tick={{ fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} domain={[0, 'dataMax + 10']} />
                            <YAxis type="category" dataKey="sentiment" tick={{ fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} width={55} />
                            <Tooltip
                                contentStyle={{ backgroundColor: theme.palette.background.paper, fontSize: responsiveFontSizes.tooltip }}
                                itemStyle={{ color: theme.palette.text.primary }}
                                formatter={(value) => [`${value} chars`, 'Avg Length']}
                            />
                            <Bar dataKey="avgLength" fill="#8884d8">
                                {avgLengthPerSentimentData.map((entry, index) => (
                                    <Cell key={`cell-avg-sent-len-${index}`} fill={entry.color} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <Typography>No data available.</Typography>
                )}
            </ChartBox>

            {/* Avg Length per Speaker Chart */}
            <ChartBox
                title="Avg Len by Speaker"
                description={descriptions.avgLenBySpeaker}
                width={chartWidth}
                height={chartHeight}
                mt={chartMarginTop}
            >
                {avgLengthPerSpeakerData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={avgLengthPerSpeakerData} margin={chartMargins.bar}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="speaker" tick={{ fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} />
                            <YAxis tick={{ fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} label={{ value: 'Avg Length', angle: -90, position: 'insideLeft', fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} />
                            <Tooltip
                                contentStyle={{ backgroundColor: theme.palette.background.paper, fontSize: responsiveFontSizes.tooltip }}
                                itemStyle={{ color: theme.palette.text.primary }}
                                formatter={(value) => [`${value} chars`, 'Avg Length']}
                            />
                            <Bar dataKey="avgLength" fill={theme.palette.secondary.main}>
                                {avgLengthPerSpeakerData.map((entry, index) => (
                                    <Cell key={`cell-avg-len-${index}`} fill={speakerColors[entry.speaker] || theme.palette.secondary.main} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <Typography>No data available.</Typography>
                )}
            </ChartBox>

            {/* Avg Seconds per Sentiment Chart */}
            <ChartBox
                title="Avg Time by Sentiment"
                description={descriptions.avgTimeBySentiment}
                width={chartWidth}
                height={chartHeight}
                mt={chartMarginTop}
            >
                {avgSecondsPerSentimentData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={avgSecondsPerSentimentData} layout="vertical" margin={{ top: 0, right: 15, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" tick={{ fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} domain={[0, 'dataMax + 1']} />
                            <YAxis type="category" dataKey="sentiment" tick={{ fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} width={55} />
                            <Tooltip
                                contentStyle={{ backgroundColor: theme.palette.background.paper, fontSize: responsiveFontSizes.tooltip }}
                                itemStyle={{ color: theme.palette.text.primary }}
                                formatter={(value) => [`${value} sec`, 'Avg Time']}
                            />
                            <Bar dataKey="avgSeconds" fill="#82ca9d">
                                {avgSecondsPerSentimentData.map((entry, index) => (
                                    <Cell key={`cell-avg-sent-sec-${index}`} fill={entry.color} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <Typography>No data available.</Typography>
                )}
            </ChartBox>

            {/* Avg Seconds per Speaker Chart */}
            <ChartBox
                title="Avg Time by Speaker"
                description={descriptions.avgTimeBySpeaker}
                width={chartWidth}
                height={chartHeight}
                mt={chartMarginTop}
            >
                {avgSecondsPerSpeakerData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={avgSecondsPerSpeakerData} margin={chartMargins.bar}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="speaker" tick={{ fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} />
                            <YAxis tick={{ fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} label={{ value: 'Avg Time', angle: -90, position: 'insideLeft', fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} />
                            <Tooltip
                                contentStyle={{ backgroundColor: theme.palette.background.paper, fontSize: responsiveFontSizes.tooltip }}
                                itemStyle={{ color: theme.palette.text.primary }}
                                formatter={(value) => [`${value} sec`, 'Avg Time']}
                            />
                            <Bar dataKey="avgSeconds" fill={theme.palette.secondary.light}>
                                {avgSecondsPerSpeakerData.map((entry, index) => (
                                    <Cell key={`cell-avg-sec-${index}`} fill={speakerColors[entry.speaker] || theme.palette.secondary.light} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <Typography>No data available.</Typography>
                )}
            </ChartBox>

            {/* Length Chart */}
            <ChartBox
                title="Sentence Length Distr."
                description={descriptions.lengthDistr}
                width={chartWidth}
                height={chartHeight}
                mt={chartMarginTop}
            >
                {lengthChartData.some(d => d.count > 0) ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={lengthChartData} margin={chartMargins.bar}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="name"
                                tick={{
                                    fontSize: responsiveFontSizes.tick,
                                    angle: -35,
                                    textAnchor: 'end',
                                    fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary
                                }}
                                interval={Math.ceil(lengthChartData.length / 15)}
                                height={40}
                            />
                            <YAxis tick={{ fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} />
                            <Tooltip
                                contentStyle={{ backgroundColor: theme.palette.background.paper }}
                                itemStyle={{ color: theme.palette.text.primary }}
                            />
                            <Bar dataKey="count" fill={LENGTH_BIN_COLOR} />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <Typography>No length data available.</Typography>
                )}
            </ChartBox>

            {/* Seconds Chart */}
            <ChartBox
                title="Sentence Time Distr."
                description={descriptions.timeDistr}
                width={chartWidth}
                height={chartHeight}
                mt={chartMarginTop}
            >
                {secondsChartData.some(d => d.count > 0) ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={secondsChartData} margin={chartMargins.bar}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="name"
                                tick={{
                                    fontSize: responsiveFontSizes.tick,
                                    fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary
                                }}
                                interval={Math.ceil(secondsChartData.length / 15)}
                            />
                            <YAxis tick={{ fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} />
                            <Tooltip
                                contentStyle={{ backgroundColor: theme.palette.background.paper }}
                                itemStyle={{ color: theme.palette.text.primary }}
                            />
                            <Bar dataKey="count" fill={SECONDS_BIN_COLOR} />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <Typography>No time data available.</Typography>
                )}
            </ChartBox>

            {/* Speaking Efficiency by Speaker Chart */}
            <ChartBox
                title="Speaking Efficiency"
                description={descriptions.speakingEfficiency}
                width={chartWidth}
                height={chartHeight}
                mt={chartMarginTop}
            >
                {speakingEfficiencyData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={speakingEfficiencyData} margin={chartMargins.bar}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="speaker" tick={{ fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} />
                            <YAxis tick={{ fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} label={{ value: 'Chars/Sec', angle: -90, position: 'insideLeft', fontSize: responsiveFontSizes.tick, fill: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary }} />
                            <Tooltip
                                contentStyle={{ backgroundColor: theme.palette.background.paper, fontSize: responsiveFontSizes.tooltip }}
                                itemStyle={{ color: theme.palette.text.primary }}
                                formatter={(value) => [`${value} chars/sec`, 'Efficiency']}
                            />
                            <Bar dataKey="efficiency" fill={theme.palette.info.main}>
                                {speakingEfficiencyData.map((entry, index) => (
                                    <Cell key={`cell-efficiency-${index}`} fill={speakerColors[entry.speaker] || theme.palette.info.main} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <Typography>No data available.</Typography>
                )}
            </ChartBox>

        </Box>
    );
};

export default ChartsGrid; 